<template>
  <div class="card">
    <div class="card-header">
      <div class="row">
        <div class="col-sm-8">
          <h4 class="title">Sales</h4>
        </div>

        <div class="col-sm-4 text-right">
          <button class="btn btn-default" @click="showTransactionsExportModal = true">
            <i class="fa fa-download mr-2"></i> Export
          </button>
        </div>
      </div>
    </div>
    <div class="card-body">
      <!-- Active Filters -->
      <active-filter-list :activeFilters="activeFilters" :tableName="tableName" @remove-filter="removeFilter"
        @clear-filters="clearFilters"></active-filter-list>

      <v-server-table :name="tableName" :url="`/v1/sales`" :columns="columns" :options="options" :ref="tableName"
        @filter="addToActiveFilters">
        <template slot="id" slot-scope="props">
          <code>{{ props.row.id }}</code>
        </template>

        <div slot="filter__capturedAt">
          <date-range-picker class="picker" ref="picker" :opens="opens" v-model="dateRange" @update="updateValues"
            style="z-index: 1; position:inherit;">
            <template v-slot:input="picker">
              {{
                picker.startDate
                ? `${picker.startDate.toISOString().split("T")[0]} -`
                : "Select date range"
              }}
              {{
                picker.endDate ? picker.endDate.toISOString().split("T")[0] : ""
              }}
            </template>
          </date-range-picker>
        </div>

        <template slot="capturedAt" slot-scope="props">
          <div>{{ props.row.capturedAt | moment("YYYY-MM-DD HH:mm") }}</div>
        </template>

        <template slot="amount" slot-scope="props">
          <span>{{ props.row.amount | money(props.row.currency) }}</span>
        </template>

        <template slot="saleAmount" slot-scope="props">
          <span>{{ props.row.saleAmount | money(props.row.currency) }}</span>
        </template>

        <template slot="gratuityAmount" slot-scope="props">
          <span>{{
            props.row.gratuityAmount | money(props.row.currency)
          }}</span>
        </template>

        <template slot="panLastFour" slot-scope="props">
          <div>************{{ props.row.panLastFour }}</div>
        </template>

        <template slot="authCode" slot-scope="props">
          <code>{{ props.row.authCode }}</code>
        </template>

        <template slot="externalReference" slot-scope="props">
          <code v-if="props.row.externalReference">{{
            props.row.externalReference
          }}</code>
        </template>

        <template slot="terminalSerialNumber" slot-scope="props">
          <terminal-fragment :terminalId="props.row.terminalId" :terminalSerialNumber="props.row.terminalSerialNumber"
            link-classes="btn btn-link btn-sm" :key="props.row.id"></terminal-fragment>
        </template>

        <template slot="userId" slot-scope="props">
          <submerchant-user-fragment v-if="props.row.userId" :submerchantId="props.row.submerchantId"
            :userId="props.row.userId" link-classes="btn btn-link btn-sm" :key="props.row.id"></submerchant-user-fragment>
        </template>

        <template slot="link" slot-scope="props">
          <router-link :to="`/transactions/sales/${props.row.id}`" class="btn btn-default btn-sm">
            <span class="d-none d-md-inline">Details &nbsp;</span>
            <i class="fa fa-chevron-right"></i>
          </router-link>
        </template>
      </v-server-table>
    </div>

    <modal :show.sync="showTransactionsExportModal" class="modal-primary" :show-close="true"
      headerClasses="justify-content-center">
      <div slot="header" class>
        <i class="fa fa-download mr-2"></i> Export transactions
      </div>

      <form>
        <div class="form-group">
          <label for="capturedAtPreset">Transaction date</label>
          <select v-model="exportOptions.capturedAtPreset" class="form-control" name="capturedAtPreset"
            id="capturedAtPreset">
            <option value="custom">Custom date range</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This week</option>
            <option value="last_week">Last week</option>
            <option value="previous_7_days">Last 7 days</option>
            <option value="this_month">This month</option>
            <option value="last_month">Last month</option>
            <option value="previous_30_days">Last 30 days</option>
            <option value="this_year">This year</option>
            <option value="last_year">Last year</option>
            <option value="previous_365_days">Last 365 days</option>
            <option value="all_time">All time</option>
          </select>
        </div>

        <div v-if="exportOptions.capturedAtPreset === 'custom'">
          <div class="form-group">
            <label class="form-label" for="capturedAtFromDate">From</label>
            <el-date-picker v-model="exportOptions.capturedAtFromDate" type="date" placeholder="Start date"
              name="capturedAtFromDate" id="capturedAtFromDate"
              :picker-options="exportDatePickerFromOptions"></el-date-picker>
          </div>

          <div class="form-group">
            <label class="form-label" for="capturedAtToDate">To</label>
            <el-date-picker v-model="exportOptions.capturedAtToDate" type="date" placeholder="End date"
              name="capturedAtToDate" id="capturedAtToDate" :picker-options="exportDatePickerToOptions"></el-date-picker>
          </div>

          <span v-if="exportDatesError" class="text-danger">Warning: {{ exportDatesError }}</span>
        </div>

        <div class="form-group">
          <label for="terminalId">Terminal</label>
          <select v-model="exportOptions.terminalId" class="form-control" name="terminalId" id="terminalId">
            <option value="all">All terminals</option>
            <option v-for="terminal in terminals" :key="terminal.id" :value="terminal.id">
              <span v-if="terminal.customReference">{{
                terminal.customReference
              }}</span>
              <span v-else>{{ terminal.serialNumber }}</span>
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="userId">User</label>
          <select v-model="exportOptions.userId" class="form-control" name="userId" id="userId">
            <option value="all">All users</option>
            <option v-for="user in users" :key="user.id" :value="user.id">
              {{ user.displayName }}
              <span v-if="user.submerchantReference">({{ user.submerchantReference }})</span>
            </option>
          </select>
        </div>

        <!-- include summary row? -->
        <div class="form-group">
          <label for="summaryRow">Summary row</label>
          <p-checkbox v-model="exportOptions.includeSummary">
            Include a summary row?
          </p-checkbox>
        </div>

        <!-- TODO: column options -->
      </form>

      <div class="alert alert-warning" v-if="exportError">
        Error exporting transactions: {{ exportError }}
      </div>

      <template slot="footer">
        <div class="left-side">
          <p-button type="default" link @click="showTransactionsExportModal = false">
            Cancel &nbsp;
            <i class="fa fa-times"></i>
          </p-button>
        </div>
        <div class="divider"></div>
        <div class="right-side">
          <p-button type="primary" link :disabled="exporting" @click="exportTransactions()">
            <span v-if="!exporting">
              Export
              <i class="fa fa-check ml-2"></i>
            </span>
            <span v-if="exporting">
              Please wait &nbsp;
              <i class="fa fa-spin fa-circle-o-notch"></i>
            </span>
          </p-button>
        </div>
      </template>
    </modal>
  </div>
</template>
<script>
import _ from "lodash";
import { doUntil, whilst, eachLimit } from "async-es";
import { DatePicker } from "element-ui";

// datepicker table filter dependencies
import jquery from "jquery";
import moment from "moment";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import { Event } from "vue-tables-2-premium";

window.$ = jquery;
window.moment = moment;

import { Button, Modal } from "src/components/UIComponents";
import { requestAdapter } from "../../../helpers/tableHelpers";
import TerminalFragment from "src/components/Dashboard/Views/Pages/Terminals/TerminalFragment.vue";
import SubmerchantUserFragment from "src/components/Dashboard/Views/Pages/Submerchants/SubmerchantUserFragment.vue";
import ActiveFilterList from "src/components/UIComponents/ActiveFilterList.vue";

const columns = [
  "amount",
  "saleAmount",
  "gratuityAmount",
  "cardScheme",
  "terminalSerialNumber",
  "userId",
  "cardType",
  "authCode",
  "panLastFour",
  "capturedAt",
  "externalReference",
  "link"
];

export default {
  name: "SalesList",
  components: {
    TerminalFragment,
    SubmerchantUserFragment,
    [Button.name]: Button,
    [DatePicker.name]: DatePicker,
    Modal,
    DateRangePicker,
    ActiveFilterList
  },
  computed: {
    operator: function () {
      return this.$store.state.operator;
    },
    exportDatesError: function () {
      if (this.exportOptions.capturedAtFromDate > new Date()) {
        return "Start date is after current date";
      }

      if (
        this.exportOptions.capturedAtFromDate &&
        this.exportOptions.capturedAtToDate
      ) {
        if (
          this.exportOptions.capturedAtToDate <
          this.exportOptions.capturedAtFromDate
        ) {
          return "End date is before Start date";
        }
      }
    }
  },
  mounted: function () {
    this.loadActiveFilters();

    this.loadAllTerminals();
    this.loadAllSubmerchantUsers();
  },
  methods: {
    loadSubmerchants: function (params, limit, offset) {
      return new Promise((resolve, reject) => {
        if (!limit) {
          limit = 100;
        }

        if (!offset) {
          offset = 0;
        }

        params = _.extend(params, {
          limit,
          offset
        });

        this.axios
          .get(`v1/submerchants`, {
            params
          })
          .then(response => {
            return resolve(response.data);
          })
          .catch(err => {
            // error - oops
            return reject(err);
          });
      });
    },
    loadAllSubmerchants: function (params) {
      const instance = this;
      return new Promise((resolve, reject) => {
        let resultCount;
        let submerchants = [];

        let offset = 0;
        const limit = 100;

        doUntil(
          next => {
            instance
              .loadSubmerchants(params, limit, offset)
              .then(result => {
                resultCount = result.length;

                submerchants = submerchants.concat(result);

                offset += limit;

                next(null, result);
              })
              .catch(err => {
                return next(err);
              });
          },
          (args, cb) => {
            return cb(null, resultCount < limit);
          }, // test passes when less results in a response than limit, iteration will stop
          err => {
            if (err) {
              console.error("Error loading submerchants: ", err);
              return reject(err);
            } else {
              return resolve(submerchants);
            }
          }
        );
      });
    },
    loadAllUsersOfSubmerchant: function (submerchantId, next) {
      const instance = this;

      // Setup pagination variables
      let offset = 0;
      const limit = 100;
      let resultCount;
      let users = [];

      // Use doUntil from async-es to handle pagination
      doUntil(
        callback => {
          instance.axios
            .get(`v1/submerchants/${submerchantId}/users`, {
              params: {
                limit,
                offset
              }
            })
            .then(response => {
              resultCount = response.data.length;

              // Add users to our collection
              users = users.concat(response.data);

              // Move to next page
              offset += limit;

              callback(null, response.data);
            })
            .catch(err => {
              callback(err);
            });
        },
        (result, cb) => {
          // Continue until we get fewer results than the limit
          return cb(null, resultCount < limit);
        },
        err => {
          if (err) {
            console.error(`Error loading users for submerchant ${submerchantId}:`, err);
            return next(err);
          } else {
            // Add all fetched users to the global users array
            instance.users = _.unionBy(instance.users, users, "id");
            return next();
          }
        }
      );
    },
    loadAllSubmerchantUsers: async function () {
      const instance = this;

      instance.options.listColumns.userId = [
        {
          id: null,
          text: "Loading..."
        }
      ];

      // load
      let offset = 0;
      const limit = 100;

      const allSubmerchants = await instance.loadAllSubmerchants({
        fields: "id"
      });
      const allSubmerchantIds = _.map(allSubmerchants, "id");

      // for each submerchant, load all users
      eachLimit(
        allSubmerchantIds,
        1,
        instance.loadAllUsersOfSubmerchant,
        err => {
          if (err) {
            console.error("Err: ", err.message);
          } else {
            // set instance.options.listColumns.userId
            instance.options.listColumns.userId = _.map(
              instance.users,
              user => {
                const item = {
                  id: user.id,
                  text: `${user.displayName}`
                };

                if (user.submerchantReference) {
                  item.text += ` (${user.submerchantReference})`;
                }

                return item;
              }
            );
          }
        }
      );
    },
    loadAllTerminals: function () {
      const instance = this;

      instance.options.listColumns.terminalSerialNumber = [
        {
          id: null,
          text: "Loading..."
        }
      ];

      // load
      let offset = 0;
      const limit = 100;

      const params = {
        fields: "id,reference,serialNumber,customReference"
      };

      let resultCount;
      let terminals = [];

      doUntil(
        next => {
          instance
            .loadTerminals(params, limit, offset)
            .then(result => {
              resultCount = result.length;

              terminals = terminals.concat(result);

              offset += limit;

              next(null, result);
            })
            .catch(err => {
              return next(err);
            });
        },
        (args, cb) => {
          return cb(null, resultCount < limit);
        }, // test passes when less results in a response than limit, iteration will stop
        err => {
          if (err) {
            console.error("Error loading terminals: ", err);
          } else {
            instance.terminals = terminals;

            instance.options.listColumns.terminalSerialNumber = _.map(
              instance.terminals,
              terminal => {
                const item = {
                  id: terminal.serialNumber,
                  text: `S/N ${terminal.serialNumber}`
                };

                if (terminal.customReference) {
                  item.text = terminal.customReference;
                }

                return item;
              }
            );
          }
        }
      ); // eslint-disable-line function-paren-newline
    },
    loadTerminals: function (params, limit, offset) {
      return new Promise((resolve, reject) => {
        if (!limit) {
          limit = 100;
        }

        if (!offset) {
          offset = 0;
        }

        params = _.extend(params, {
          limit,
          offset
        });

        this.axios
          .get(`v1/terminals`, {
            params
          })
          .then(response => {
            return resolve(response.data);
          })
          .catch(err => {
            // error - oops
            return reject(err);
          });
      });
    },
    exportTransactions: function () {
      const instance = this;
      instance.exporting = true;

      // build query
      let query = "?";

      let startDate = null;
      let endDate = null;
      if (instance.exportOptions.capturedAtPreset === "custom") {
        startDate = moment(instance.exportOptions.capturedAtFromDate).startOf(
          "day"
        );

        endDate = moment(instance.exportOptions.capturedAtToDate).endOf("day");
      } else if (instance.exportOptions.capturedAtPreset === "today") {
        startDate = moment().startOf("day");

        endDate = moment().endOf("day");
      } else if (instance.exportOptions.capturedAtPreset === "yesterday") {
        startDate = moment()
          .subtract(1, "day")
          .startOf("day");

        endDate = moment()
          .subtract(1, "day")
          .endOf("day");
      } else if (instance.exportOptions.capturedAtPreset === "this_week") {
        startDate = moment().startOf("week");

        endDate = moment();
      } else if (instance.exportOptions.capturedAtPreset === "last_week") {
        startDate = moment()
          .subtract(1, "week")
          .startOf("week");

        endDate = moment()
          .subtract(1, "week")
          .endOf("week");
      } else if (
        instance.exportOptions.capturedAtPreset === "previous_7_days"
      ) {
        startDate = moment()
          .subtract(7, "days")
          .startOf("day");

        endDate = moment();
      } else if (instance.exportOptions.capturedAtPreset === "this_month") {
        startDate = moment().startOf("month");

        endDate = moment();
      } else if (instance.exportOptions.capturedAtPreset === "last_month") {
        startDate = moment()
          .subtract(1, "month")
          .startOf("month");

        endDate = moment()
          .subtract(1, "month")
          .endOf("month");
      } else if (
        instance.exportOptions.capturedAtPreset === "previous_30_days"
      ) {
        startDate = moment()
          .subtract(30, "days")
          .startOf("day");

        endDate = moment();
      } else if (instance.exportOptions.capturedAtPreset === "this_year") {
        startDate = moment().startOf("year");

        endDate = moment();
      } else if (instance.exportOptions.capturedAtPreset === "last_year") {
        startDate = moment()
          .subtract(1, "year")
          .startOf("year");

        endDate = moment()
          .subtract(1, "year")
          .endOf("year");
      } else if (
        instance.exportOptions.capturedAtPreset === "previous_365_days"
      ) {
        startDate = moment()
          .subtract(365, "days")
          .startOf("day");

        endDate = moment();
      }

      // set to query string
      if (
        instance.exportOptions.capturedAtPreset !== "all_time" &&
        startDate &&
        endDate
      ) {
        query += `capturedAt[$gte]=${startDate.toISOString()}`;

        query += `&capturedAt[$lt]=${endDate.toISOString()}`;
      }

      // terminal ID
      if (
        !instance.exportOptions.terminalId ||
        instance.exportOptions.terminalId !== "all"
      ) {
        query += `&terminalId=${instance.exportOptions.terminalId}`;
      }

      // user ID
      if (
        !instance.exportOptions.userId ||
        instance.exportOptions.userId !== "all"
      ) {
        query += `&userId=${instance.exportOptions.userId}`;
      }

      let sales = [];

      let totalAvailable = null;
      let totalCountIsApproximate = false;
      let latestRequestReturnedResults = false;

      let offset = 0;
      const limit = 100;
      whilst(
        function test(cb) {
          if (instance.showTransactionsExportModal === false) {
            // user cancelled the operation
            return cb(null, false);
          }

          if (totalAvailable === null && totalCountIsApproximate === false) {
            // first run
            return cb(null, true);
          } else if (
            totalAvailable === null &&
            totalCountIsApproximate === true
          ) {
            // total count is approximate, so we need to check whether the latest request has returned any results
            return cb(null, latestRequestReturnedResults);
          } else {
            return cb(null, sales.length < totalAvailable);
          }
        },
        function iter(callback) {
          instance.axios
            .get(`v1/sales${query}&offset=${offset}&limit=${limit}`)
            .then(response => {
              sales = sales.concat(response.data);

              if (totalAvailable === null) {
                if (
                  response.headers["total-count"] &&
                  response.headers["total-count-is-approximate"] === "false"
                ) {
                  totalAvailable = parseInt(response.headers["total-count"]);
                  totalCountIsApproximate = false;
                } else {
                  totalCountIsApproximate = true;
                }
              }

              if (response.data.length) {
                latestRequestReturnedResults = true;
              } else {
                latestRequestReturnedResults = false;
              }

              offset += limit;

              return callback(null);
            })
            .catch(err => {
              // error - opps
              console.error("Error: ", err); // TODO: set to error
              return callback(err);
            });
        },
        function (err, n) {
          if (err) {
            // set err to error
          } else {
            // completion without error
            instance.exporting = false;

            if (instance.showTransactionsExportModal === false) {
              // user cancelled the operation, do nothing (could notify: cancelled)
            } else {
              // manipulate data (select and format rows)
              const summaries = {
                amount: 0,
                saleAmount: 0,
                gratuityAmount: 0
              };
              const formattedSales = sales.map(sale => {
                const result = [
                  sale.id,
                  sale.amount / 100,
                  sale.saleAmount / 100,
                  sale.gratuityAmount / 100,
                  sale.currency.toUpperCase(),
                  sale.authCode,
                  sale.retrievalReferenceNumber,
                  moment(sale.capturedAt).format("YYYY-MM-DDTHH:mm:ss"), // format in local time
                  sale.terminalSerialNumber,
                  sale.panLastFour,
                  sale.cardScheme,
                  sale.cardType,
                  sale.externalReference
                ];

                // match up terminal
                const terminal = _.find(instance.terminals, {
                  id: sale.terminalId
                });
                if (terminal) {
                  result.push(terminal.customReference);
                } else {
                  result.push(null);
                }

                // match up user
                const user = _.find(instance.users, {
                  id: sale.userId
                });
                if (user) {
                  result.push(user.id);
                  result.push(user.displayName);
                  result.push(user.submerchantReference);
                } else {
                  result.push(null);
                  result.push(null);
                  result.push(null);
                }

                summaries.amount += sale.amount;
                summaries.saleAmount += sale.saleAmount;
                summaries.gratuityAmount += sale.gratuityAmount;

                return result;
              });

              // add summary if selected
              if (instance.exportOptions.includeSummary) {
                formattedSales.push([
                  "SUMMARY",
                  summaries.amount / 100,
                  summaries.saleAmount / 100,
                  summaries.gratuityAmount / 100,
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  ""
                ]);
              }

              // write to CSV
              const csv = instance.$papa.unparse(
                {
                  fields: [
                    "ID",
                    "Total Amount",
                    "Fare Amount",
                    "Tip Amount",
                    "Currency",
                    "Auth Code",
                    "RRN",
                    "Transaction Date",
                    "Terminal Serial Number",
                    "PAN ending",
                    "Card Scheme",
                    "Card Type",
                    "External Reference",
                    "Terminal Custom Reference",
                    "User ID",
                    "User Name",
                    "User Reference"
                  ],
                  data: formattedSales
                },
                {
                  delimiter: ",",
                  quotes: [
                    true, // ID
                    false, // total amount
                    false, // sale amount
                    false, // tip amount
                    false, // currency
                    true, // auth code
                    true, // RRN
                    false, // transaction date
                    true, // TSN
                    true, // PAN ending
                    false, // scheme
                    false, // type
                    true, // external reference
                    true, // terminal custom reference
                    true, // user ID
                    true, // user name
                    true // user reference
                  ]
                }
              );
              // Trigger "download"
              instance.$papa.download(csv, "Transactions Export");

              // Show swal alert
              instance.$swal({
                icon: "success",
                title: "Export complete",
                text: `Your file download has started automatically.`
              });

              // close modal
              instance.showTransactionsExportModal = false;
            }
          }
        }
      );
    },
    updateValues(vals) {
      let filter = { name: "date", value: vals };
      let isDuplicate = this.containsObject(filter, this.activeFilters);
      if (!isDuplicate) {
        this.activeFilters.push(filter);
      }
      Event.$emit("vue-tables.salesList.filter::capturedAt", {
        start: moment(vals.startDate).startOf("day"),
        end: moment(vals.endDate).endOf("day")
      });
    },
    clearFilters() {
      this.dateRange.startDate = null;
      this.dateRange.endDate = null;
      this.$refs["salesList"].resetQuery();
      this.$refs["salesList"].resetCustomFilters();
      this.activeFilters = [];

      Event.$emit("vue-tables.salesList.filter::capturedAt", "");
    },
    containsObject(obj, list) {
      for (const i in list) {
        if (obj.name === list[i].name) {
          return true;
        }
      }

      return false;
    },
    removeFilter(filter) {
      if (filter.name === "date") {
        filter.name = "capturedAt";
        Event.$emit(`vue-tables.salesList.filter::${filter.name}`, "");
      } else {
        const filterObj = {};
        filterObj[filter.name] = "";
        this.$refs.salesList.setFilter(filterObj);
      }
    },
    addToActiveFilters(filter) {
      let isDuplicate = this.containsObject(filter, this.activeFilters);

      if (filter.value !== "" && !isDuplicate) {
        this.activeFilters.push(filter);
      } else if (filter.value === "") {
        const index = this.activeFilters.indexOf(filter);
        this.activeFilters.splice(index, 1);
      }
    },
    loadActiveFilters() {
      const tableData = this.$refs[this.tableName].getRequestParams();
      const activeQueries = tableData["query"];

      if (tableData['capturedAt'] !== null || '') {
        activeQueries.date = tableData['capturedAt']
      }

      for (let query in activeQueries) {
        if (activeQueries[query] !== "") {
          const queryObj = { name: query, value: activeQueries[query] };
          this.activeFilters.push(queryObj);
        }
      }
    }
  },
  data() {
    return {
      showTransactionsExportModal: false,
      exporting: false,
      exportError: null,
      exportOptions: {
        capturedAtFromDate: null,
        capturedAtToDate: null,
        capturedAtPreset: "last_month",
        terminalId: "all",
        userId: "all",
        columns: [
          "amount",
          "saleAmount",
          "gratuityAmount",
          "cardScheme",
          "terminalSerialNumber",
          "userId",
          "cardType",
          "panLastFour",
          "capturedAt"
        ],
        includeSummary: false
      },
      exportDatePickerFromOptions: {
        disabledDate(date) {
          // date must be today or earlier
          return date > new Date();
        }
      },
      exportDatePickerToOptions: {
        disabledDate(date) {
          // date must be today or earlier
          if (date < new Date()) {
            return false;
          }
        }
      },
      terminals: [],
      users: [],
      columns,
      tableName: "salesList",
      activeFilters: [],
      options: {
        name: this.tableName,
        filterByColumn: true,
        columnsDropdown: false,
        columnsDisplay: {
          amount: "min_mobileP",
          saleAmount: "min_tabletL",
          gratuityAmount: "min_tabletL",
          cardScheme: "min_desktop",
          terminalSerialNumber: "min_desktop",
          cardType: "min_desktop",
          authCode: "min_desktop",
          panLastFour: "min_tabletL",
          capturedAt: "min_mobileL",
          link: "min_mobile"
        },
        headings: {
          link: "",
          amount: "Total",
          saleAmount: "Fare",
          gratuityAmount: "Tip",
          cardScheme: "Scheme",
          terminalSerialNumber: "Terminal",
          userId: "User",
          cardType: "Card type",
          authCode: "Auth code",
          panLastFour: "PAN (last four)",
          capturedAt: "Date",
          externalReference: "Reference"
        },
        sortable: ["amount", "saleAmount", "gratuityAmount", "capturedAt"],
        customFilters: ["capturedAt"],
        filterable: [
          "cardScheme",
          "cardType",
          "panLastFour",
          "authCode",
          "terminalSerialNumber",
          "userId",
          "amount",
          "externalReference"
        ],
        listColumns: {
          terminalSerialNumber: [],
          userId: [],
          cardScheme: [
            {
              id: "visa",
              text: "Visa"
            },
            {
              id: "mastercard",
              text: "Mastercard"
            },
            {
              id: "american_express",
              text: "American Express"
            }
          ],
          cardType: [
            {
              id: "credit",
              text: "Credit"
            },
            {
              id: "debit",
              text: "Debit"
            }
          ]
        },
        pagination: { chunk: 3, dropdown: false },
        requestAdapter
      },
      dateRange: {
        startDate: null,
        endDate: null
      },
      opens: "left"
    };
  }
};
</script>
<style>
.picker {
  text-transform: none;
  font-weight: normal;
}

.vue-daterange-picker {
  width: 100%;
}

.reportrange-text[data-v-1ebd09d2] {
  border: 1px solid #ddd;
  margin-top: 5px;
  padding: 7px 5px;
  overflow: hidden;
  white-space: nowrap;
}
</style>
